import React from "react";
import { SxProps, TextField, Typography, Theme } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { CategoryFindingSummary, LensRuleType } from "Backend";
import StyledAutocomplete from "components/StyledAutocomplete";

export interface CaseHitsLensFilterProps {
  label: string;
  showCounts: boolean;
  options: CategoryFindingSummary[];
  value: CategoryFindingSummary[];
  onChange: (ruleIds: string[]) => void;
  groupBy?: (finding: CategoryFindingSummary) => string;
  sx?: SxProps<Theme>;
}

function itemLabel(rt: LensRuleType): string {
  return rt.subtype + " - " + rt.description;
}

const renderItemOption = (item: CategoryFindingSummary, showCount: boolean) => (
  <Typography noWrap variant="subtitle2" style={{ fontSize: "0.7rem" }}>
    {item.ruleType.description + (showCount ? " (" + item.count + ")" : "")}
  </Typography>
);

const CaseHitsLensFilter = (props: CaseHitsLensFilterProps) => {
  const { label } = props;
  const items = React.useMemo(
    () =>
      props.options.slice(0).sort((i1, i2) => {
        const nameCmp = i1.ruleType.description.localeCompare(i2.ruleType.description);
        if (props.groupBy) {
          const grpCmp = props.groupBy(i1).localeCompare(props.groupBy(i2));
          if (grpCmp === 0) {
            return nameCmp;
          } else return grpCmp;
        } else return nameCmp;
      }),
    [props.options, props.groupBy]
  );

  return (
    <StyledAutocomplete
      autoHighlight
      size="small"
      style={{ fontSize: "0.75rem" }}
      multiple
      options={items}
      onChange={(_evt, items) => {
        const selections = items.filter((i) => typeof i !== "string") as CategoryFindingSummary[];
        const selectedIds = selections.map((c) => c.categoryId);
        props.onChange(selectedIds);
      }}
      value={props.value}
      sx={props.sx}
      groupBy={props.groupBy}
      renderOption={(p: React.HTMLAttributes<HTMLLIElement>, item: CategoryFindingSummary) => (
        <li {...p}>{renderItemOption(item, props.showCounts)}</li>
      )}
      getOptionLabel={(i) => (typeof i === "string" ? i : itemLabel(i.ruleType))}
      filterOptions={createFilterOptions({
        ignoreCase: true,
        matchFrom: "any",
        stringify: (i) => i.ruleType.subtype + i.ruleType.description,
      })}
      renderInput={(params) => {
        return <TextField {...params} label={label} fullWidth />;
      }}
    />
  );
};

export default CaseHitsLensFilter;
